import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';
import { type Person } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { autoDownloadLink, getPrettyDate } from 'utils/util/util';
import Page from 'views/Layout/Pages/Page';
import ObjectsDropdown from 'views/Widgets/Dropdowns/ObjectsDropdown';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import CustomSurveyResponsesParticipantsTab, {
  type OverviewParticipant,
} from './CustomSurveyResponsesParticipantsTab';
import CustomSurveyResponsesQuestionsTab, {
  type OverviewQuestion,
} from './CustomSurveyResponsesQuestionsTab';
import {
  SURVEY_RESPONSES_DEFAULT_TAB,
  SURVEY_RESPONSES_TAB_PARAM,
  SURVEY_RESPONSES_TABS,
  type SurveyResponsesTab,
} from './consts';

interface RouteParams {
  id: string;
}

export interface SurveyInstance {
  id: number;
  survey_name: string;
  survey_type: string;
  // The instant the instance represents, which is what reporting groups and
  // labels by.
  scheduled_datetime: string;
  created_at: string;
}

interface OverviewResponse {
  participants: OverviewParticipant[];
  questions: OverviewQuestion[];
}

type ExportScope = 'instance' | 'survey';

const CustomSurveyResponsesPage: FC = () => {
  const { id } = useParams<RouteParams>();
  const { formatMessage, locale } = useIntl();
  const history = useHistory();
  const location = useLocation();
  const instanceFromUrl = useMemo(() => {
    const param = new URLSearchParams(location.search).get('instance');
    const parsed = param ? parseInt(param, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [location.search]);

  const [selectedInstanceId, setSelectedInstanceId] = useState<
    number | undefined
  >(instanceFromUrl);

  // Kept in the URL (`?tab=`) rather than local state so a tab is directly
  // linkable.
  const tabParam = new URLSearchParams(location.search).get(
    SURVEY_RESPONSES_TAB_PARAM
  );

  const tab: SurveyResponsesTab =
    SURVEY_RESPONSES_TABS.find((candidate) => candidate === tabParam) ??
    SURVEY_RESPONSES_DEFAULT_TAB;

  const setTab = (next: SurveyResponsesTab) => {
    const params = new URLSearchParams(location.search);
    params.set(SURVEY_RESPONSES_TAB_PARAM, next);
    history.replace({ search: params.toString() });
  };

  const [isExportOpen, setIsExportOpen] = useState(false);
  const [exportScope, setExportScope] = useState<ExportScope | undefined>(
    undefined
  );
  const [csvTaskDescriptor, setCsvTaskDescriptor] = useState<
    { task_key: string; status: string } | undefined
  >(undefined);

  const { data: instances, status: instancesStatus } = useConfirmApi<
    SurveyInstance[]
  >({
    method: 'GET',
    url: `custom-survey/surveys/${id}/instances`,
  });

  useEffect(() => {
    if (instances && selectedInstanceId === undefined) {
      setSelectedInstanceId(instances[0]?.id);
    }
  }, [instances, selectedInstanceId]);

  const { data: overview, status: overviewStatus } =
    useConfirmApi<OverviewResponse>({
      method: 'GET',
      url: selectedInstanceId
        ? `custom-survey/instances/${selectedInstanceId}/responses-overview`
        : '',
      disabled: !selectedInstanceId,
      clearOnChange: true,
    });

  const instanceObjects = useMemo(() => {
    const list = instances ?? [];
    // The day the instance is for, not when its row was written - those
    // diverge for a backfill and for any late scheduler run.
    const dates = list.map((inst) =>
      getPrettyDate({
        dateString: inst.scheduled_datetime,
        includeTime: false,
        locale,
      })
    );
    const countByDate = dates.reduce<Record<string, number>>((acc, date) => {
      acc[date] = (acc[date] ?? 0) + 1;
      return acc;
    }, {});

    return list.map((inst, index) => {
      const date = dates[index];
      // A survey can send more than once on the same date, and then the date
      // alone names every one of them the same. The time of day is what tells
      // those apart.
      const name =
        countByDate[date] > 1
          ? `${date}, ${new Date(inst.scheduled_datetime).toLocaleTimeString(
              locale,
              { hour: 'numeric', minute: '2-digit' }
            )}`
          : date;
      return { id: inst.id, name };
    });
  }, [instances, locale]);

  const onSelectInstance = useCallback(
    (e) => {
      const next = parseInt(e.target.value, 10);
      setSelectedInstanceId(next);
      const params = new URLSearchParams(location.search);
      params.set('instance', String(next));
      history.replace({ search: params.toString() });
    },
    [history, location.search]
  );

  const handleExportError = useCallback(
    (error: object) => {
      setExportScope(undefined);
      setCsvTaskDescriptor(undefined);
      console.error('Error exporting survey responses:', error);
      toast.error(
        formatMessage({
          id: 'app.views.administration.custom_surveys.responses.export_error',
          defaultMessage: 'Could not export survey responses.',
        })
      );
    },
    [formatMessage]
  );

  const handleExportCompleted = useCallback(
    (output: BlobPart) => {
      const safeName = (instances?.[0]?.survey_name ?? 'survey').replace(
        /[^\w.-]+/g,
        '_'
      );
      const suffix =
        exportScope === 'instance' ? `_instance_${selectedInstanceId}` : '_all';
      setExportScope(undefined);
      setCsvTaskDescriptor(undefined);
      autoDownloadLink({
        data: output,
        filename: `${safeName}${suffix}_responses.csv`,
        mime: 'text/csv',
      });
    },
    [instances, exportScope, selectedInstanceId]
  );

  useBackgroundTask({
    taskDescriptor: csvTaskDescriptor,
    onTaskCompleted: handleExportCompleted,
    onError: handleExportError,
  });

  const onExportSubmitted = useCallback(
    (data: { task_key?: string } | null, error) => {
      if (error || !data?.task_key) {
        handleExportError(error);
        return;
      }
      setCsvTaskDescriptor({ task_key: data.task_key, status: 'submitted' });
    },
    [handleExportError]
  );

  const exportParams = useMemo(
    () => (exportScope === 'instance' ? { instance: selectedInstanceId } : {}),
    [exportScope, selectedInstanceId]
  );

  const isExporting = !!exportScope;

  // This request only enqueues the export; polling delivers the file.
  useConfirmApi({
    method: 'GET',
    url: `custom-survey/surveys/${id}/responses-export`,
    params: exportParams,
    disabled: !exportScope || !!csvTaskDescriptor,
    callback: onExportSubmitted,
  });

  const handleBackToList = useCallback(() => {
    history.push(
      consts.ADMINISTRATION_CUSTOM_SURVEYS_SURVEYS(formatMessage).path
    );
  }, [history, formatMessage]);

  const goToQuestion = useCallback(
    (uuid: string) => {
      if (!selectedInstanceId) return;
      history.push(
        consts
          .ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES_QUESTION(formatMessage)
          .path.replace(':id', id)
          .replace(':instanceId', String(selectedInstanceId))
          .replace(':uuid', uuid)
      );
    },
    [history, formatMessage, id, selectedInstanceId]
  );

  const goToParticipant = useCallback(
    (person: Person) => {
      if (!selectedInstanceId) return;
      history.push(
        {
          pathname: consts
            .ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES_PARTICIPANT(formatMessage)
            .path.replace(':id', id)
            .replace(':instanceId', String(selectedInstanceId))
            .replace(':personId', String(person.id)),
        },
        { person }
      );
    },
    [history, formatMessage, id, selectedInstanceId]
  );

  const backButton = (
    <Button color="link" onClick={handleBackToList}>
      <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
      <FormattedMessage
        id="app.views.administration.custom_surveys.responses.back"
        defaultMessage="Back to surveys"
      />
    </Button>
  );

  if (instancesStatus === 'LOADING') {
    return <Loading />;
  }

  if (instancesStatus === 'ERROR' || !instances) {
    return (
      <Page>
        <>
          <div className="d-flex align-items-center mt-4 mb-3">
            {backButton}
          </div>
          <Card>
            <CardBody>
              <EmptyState
                title={formatMessage({
                  id: 'app.views.administration.custom_surveys.responses.load_error',
                  defaultMessage: 'Could not load survey responses.',
                })}
              />
            </CardBody>
          </Card>
        </>
      </Page>
    );
  }

  if (instances.length === 0) {
    return (
      <Page>
        <>
          <div className="d-flex align-items-center mt-4 mb-3">
            {backButton}
          </div>
          <Card>
            <CardBody className="text-center text-muted">
              <FormattedMessage
                id="app.views.administration.custom_surveys.responses.no_instances"
                defaultMessage="This survey has not run yet, so there are no responses to view."
              />
            </CardBody>
          </Card>
        </>
      </Page>
    );
  }

  return (
    <Page plainTextTitle={instances[0]?.survey_name ?? ''}>
      <>
        <div className="position-relative d-flex align-items-center mt-4 mb-4">
          {backButton}
          <h2 className="mb-0 position-absolute start-50 translate-middle-x">
            {instances[0]?.survey_name ?? ''}
          </h2>
        </div>

        <Row className="mb-4 align-items-end">
          <Col className="col-auto">
            <label className="form-label text-muted small mb-1">
              <FormattedMessage
                id="app.views.administration.custom_surveys.responses.instance_label"
                defaultMessage="Instance"
              />
            </label>
            <ObjectsDropdown
              objects={instanceObjects}
              value={selectedInstanceId}
              onChange={onSelectInstance}
            />
          </Col>
          <Col className="col-auto ms-auto">
            <Dropdown
              isOpen={isExportOpen}
              toggle={() => setIsExportOpen((prev) => !prev)}
            >
              <DropdownToggle color="light" caret disabled={isExporting}>
                <Icon className="me-1" icon={consts.ICONS.EXPORT} />
                {isExporting ? (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.responses.exporting"
                    defaultMessage="Preparing CSV…"
                  />
                ) : (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.responses.export"
                    defaultMessage="Export to CSV"
                  />
                )}
              </DropdownToggle>
              <DropdownMenu end>
                <DropdownItem
                  onClick={() => setExportScope('instance')}
                  disabled={isExporting || !selectedInstanceId}
                >
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.responses.export_instance"
                    defaultMessage="This instance"
                  />
                </DropdownItem>
                {/* With a single instance this would export the same rows as
                    "This instance", so it is not offered. */}
                {instances.length > 1 && (
                  <DropdownItem
                    onClick={() => setExportScope('survey')}
                    disabled={isExporting}
                  >
                    <FormattedMessage
                      id="app.views.administration.custom_surveys.responses.export_survey"
                      defaultMessage="Entire survey (all instances)"
                    />
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
          </Col>
        </Row>

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              href="#"
              active={tab === 'participants'}
              onClick={(e) => {
                e.preventDefault();
                setTab('participants');
              }}
            >
              <FormattedMessage
                id="app.views.administration.custom_surveys.responses.tab.participants"
                defaultMessage="Participants"
              />
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              href="#"
              active={tab === 'questions'}
              onClick={(e) => {
                e.preventDefault();
                setTab('questions');
              }}
            >
              <FormattedMessage
                id="app.views.administration.custom_surveys.responses.tab.questions"
                defaultMessage="Questions"
              />
            </NavLink>
          </NavItem>
        </Nav>

        {overviewStatus === 'LOADING' && <Loading />}

        {overviewStatus === 'ERROR' && (
          <Card>
            <CardBody>
              <EmptyState
                title={formatMessage({
                  id: 'app.views.administration.custom_surveys.responses.overview_error',
                  defaultMessage: 'Could not load responses for this instance.',
                })}
              />
            </CardBody>
          </Card>
        )}

        {overview && tab === 'questions' && (
          <CustomSurveyResponsesQuestionsTab
            questions={overview.questions}
            onSelectQuestion={goToQuestion}
          />
        )}

        {overview && tab === 'participants' && (
          <CustomSurveyResponsesParticipantsTab
            participants={overview.participants}
            onSelectParticipant={goToParticipant}
          />
        )}
      </>
    </Page>
  );
};

export default CustomSurveyResponsesPage;
